import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import dayjs from 'dayjs';
import { SilentError } from 'init/silent-error';
import { isDefined, omit } from 'remeda';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  type QueryOptions,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';

// #region Variables
const LoadUnhandledClientVariablesSchema = z.object({
  debtorId: z.coerce.string(),
});

export type LoadUnhandledClientVariables = z.infer<typeof LoadUnhandledClientVariablesSchema>;
// #endregion Variables

// #region Response
export const ActionTypeSchema = z.enum([
  'bailiff',
  'debt_collection',
  'lawyer',
  'formal_notice',
  'additional_reminder',
  'mark_as_paid',
  'mark_as_unrecoverable',
  'mark_as_disputed',
  'payment_plan',
  'direct_contact',
]);

export const InvoiceActionSchema = z
  .object({
    type: ActionTypeSchema,
    priority: z.number(),
    availability: z.boolean(),
  })
  .transform(data => ({
    ...omit(data, ['availability']),
    isAvailable: data.availability,
  }));

const InvoiceStatusKeySchema = z.enum([
  'off_balance',
  'draft',
  'paid',
  'not_late',
  'disputed',
  'unrecoverable',
  'paused',
  'open_bailiff_case',
  'open_lawyer_case',
  'open_callcenter_case',
  'open_debt_collector_case',
  'formal_notice_sent',
  'last_reminder_sent',
  'first_reminder_sent',
  'imported_late',
  'late',
]);

export const UnhandledInvoiceSchema = z.object({
  id: z.coerce.string(),
  reference: z.string(),
  totalTvac: z.coerce.number(),
  dueDate: z.string().transform(dayjs),
  status: z.string().optional(),
  statusKey: InvoiceStatusKeySchema.optional().catch(undefined),
  availableActions: z.array(ActionTypeSchema).optional(),
});

const OtherInvoiceSchema = z.object({
  id: z.coerce.string(),
  reference: z.string(),
  totalTvac: z.coerce.number(),
});

export const UnhandledClientSchema = z.object({
  recommendedActions: z.array(InvoiceActionSchema),
  unhandledInvoices: z.array(UnhandledInvoiceSchema),
  underActiveRecoveryInvoices: z.array(OtherInvoiceSchema),
  paymentPlanInvoices: z.array(OtherInvoiceSchema),
  notDueInvoices: z.array(OtherInvoiceSchema),
  thirdPartyInvoices: z.array(OtherInvoiceSchema),
});

export type ActionType = z.infer<typeof ActionTypeSchema>;
export type InvoiceStatusKey = z.infer<typeof InvoiceStatusKeySchema>;
export type InvoiceAction = z.infer<typeof InvoiceActionSchema>;
export type UnhandledInvoice = z.infer<typeof UnhandledInvoiceSchema>;
export type UnhandledClient = z.infer<typeof UnhandledClientSchema>;
// #endregion Response

// MARK: Load function
export async function loadUnhandledClientQueryFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: LoadUnhandledClientVariables,
) {
  const instance = await axiosInstance;
  const { data } = await instance.get(`/debtors/${variables.debtorId}/unhandled_invoices`);

  const parseResult = UnhandledClientSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'unhandledClient');
    throw new SilentError();
  }

  return parseResult.data;
}

// MARK: Hook
const emptyUnhandledClient: UnhandledClient = {
  recommendedActions: [],
  unhandledInvoices: [],
  underActiveRecoveryInvoices: [],
  paymentPlanInvoices: [],
  notDueInvoices: [],
  thirdPartyInvoices: [],
};

export function useLoadUnhandledClient(
  variables: LoadUnhandledClientVariables,
  options: QueryOptions<UnhandledClient> = {},
) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<UnhandledClient, ApiError>({
    queryKey: ['unhandledClient', variables],
    queryFn: () => loadUnhandledClientQueryFn(axiosInstance, variables),
    placeholderData: previousData => {
      if (isDefined(previousData)) return previousData;
      return emptyUnhandledClient;
    },
    ...options,
  });

  return addResourceNameToQueryResult<
    UnhandledClient,
    ApiError,
    'unhandledClient',
    'PreventUndefined'
  >('unhandledClient', queryResult, emptyUnhandledClient);
}
